import { journalInputs } from "./blackBox";
import { contentHash } from "../content/rules";
import { LocalMatch } from "./localMatch";
import { reachScenario } from "./reachScenario";
import { OnlineMatch } from "./onlineMatch";
import { SCENARIOS, type Scenario } from "./scenarios";
import { startLoop } from "./loop";

/** Вход игры. Параметры адреса: ?bots=1 — оба боты, ?bot=2 — второй бот, ?seed=N, ?capture=1 — съёмка по кадрам. */
/** Постановочная сцена по имени: из списка или обмер касания reach-<приём> (app/reachScenario.ts). */
const scenarioNamed = (name: string): Scenario | undefined => SCENARIOS[name] ?? reachScenario(name);

const params = new URLSearchParams(window.location.search);
const found = document.querySelector<HTMLCanvasElement>("#screen");
if (!found) throw new Error("Нет холста #screen");
const canvas: HTMLCanvasElement = found;

const bothBots = params.get("bots") === "1";
const secondBot = params.get("bot") === "2";
const seed = Number(params.get("seed") ?? "1") || 1;
const room = params.get("room");
const online = params.get("online") === "1" || room !== null;

/** Ссылка для соперника — в обычном тексте страницы: её можно выделить и скопировать. */
function showLink(link: string | null): void {
  const panel = document.querySelector<HTMLElement>("#lobby");
  const field = document.querySelector<HTMLInputElement>("#lobby-link");
  if (!panel || !field) return;
  panel.hidden = link === null;
  field.value = link ?? "";
}
document.querySelector("#lobby-copy")?.addEventListener("click", () => {
  const field = document.querySelector<HTMLInputElement>("#lobby-link");
  if (!field) return;
  void navigator.clipboard.writeText(field.value).catch(() => {
    field.select();
  });
});

const signalUrl =
  (import.meta.env.VITE_SIGNAL_URL as string | undefined) ??
  `${window.location.protocol === "https:" ? "wss" : "ws"}://${window.location.hostname}:8787`;
const match = online
  ? new OnlineMatch(canvas, window, {
      room,
      signalUrl,
      bot: bothBots,
      lagMs: Number(params.get("lag") ?? "0") || 0,
      inputDelay: params.get("delay") ? Math.max(1, Math.min(4, Number(params.get("delay")) || 2)) : null,
      relay: params.get("relay") === "1",
      onLink: showLink,
    })
  : new LocalMatch(canvas, window, {
      bots: [bothBots, bothBots || secondBot],
      seed,
      ...(params.get("meter") ? { meter: Math.max(0, Math.min(3000, Number(params.get("meter")) || 0)) } : {}),
      // переигровка «чёрного ящика»: журнал кладёт съёмка (tools/capture/capture.mjs --journal)
      ...(params.get("capture") === "1" && window.__replay ? { replay: journalInputs(window.__replay) } : {}),
      // бой человеком на этом ПК — «чёрный ящик» пишется сам
      ...(params.get("capture") !== "1" ? { journal: true } : {}),
      ...(params.get("capture") === "1" ? { recordAudio: true } : {}),
      ...(params.get("capture") === "1" && scenarioNamed(params.get("scenario") ?? "")
        ? { scenario: scenarioNamed(params.get("scenario") ?? "") as Scenario }
        : {}),
    });

/**
 * Холст — 1920×1080. В полном экране 1080p — один к одному, на 4K — ×2 точно. Окно меньше Full HD — уменьшение
 * с мягким сглаживанием: целого масштаба вниз не бывает, клавиша F — полный экран.
 */
function fit(): void {
  const scale = Math.min(window.innerWidth / canvas.width, window.innerHeight / canvas.height);
  const whole = scale >= 1 ? Math.floor(scale) : scale;
  canvas.style.width = `${Math.floor(canvas.width * whole)}px`;
  canvas.style.height = `${Math.floor(canvas.height * whole)}px`;
  canvas.style.imageRendering = scale >= 1 ? "pixelated" : "auto";
}
window.addEventListener("keydown", (event) => {
  if (event.code !== "KeyF") return;
  if (document.fullscreenElement) void document.exitFullscreen();
  else void document.documentElement.requestFullscreen();
});
window.addEventListener("resize", fit);
// Клик по холсту возвращает фокус странице: клавиатура снова слышна
canvas.addEventListener("pointerdown", () => {
  window.focus();
});
window.focus();
fit();

declare global {
  interface Window {
    __online?: { report(): ReturnType<OnlineMatch["report"]>; link(): string };
    __local?: { report(): ReturnType<LocalMatch["report"]> };
    __replay?: { inputs?: number[]; runs?: number[]; seed: number; meter: number };
    __arena?: {
      advance(frames: number): void;
      /** Поставить камеру на место сразу — после пропуска кадров съёмка не начинается с наезда. */
      settle(): void;
      frame(): number;
      fighters(): {
        x: number;
        y: number;
        state: number;
        health: number;
        ability: number;
        abilityFrame: number;
        stateFrame: number;
        slowmo: number;
        slowTick: number;
      }[];
      measurements(): { camera: number; contrast: readonly [number, number]; cinematic: number };
      probe(): { visible: boolean; hips: number[]; bones: number[][] }[];
      sparks(): ReturnType<LocalMatch["sparks"]>;
      reach(): ReturnType<LocalMatch["reachTable"]>;
      audioMark(): void;
      audioTrack(): Promise<string>;
      audioCues(): ReturnType<LocalMatch["audioCues"]>;
      reactions(frames: number): ReturnType<LocalMatch["reactionTable"]>;
      limbsAt(clip: string, frame: number): ReturnType<LocalMatch["limbsAt"]>;
      art(): string;
      background(): string;
      ready(): boolean;
      content: string;
    };
  }
}

if (match instanceof OnlineMatch) {
  const unlockOnline = (): void => {
    match.unlockAudio();
  };
  for (const kind of ["keydown", "pointerdown", "touchstart"] as const) window.addEventListener(kind, unlockOnline);
  window.__online = {
    report: () => match.report(),
    link: () => document.querySelector<HTMLInputElement>("#lobby-link")?.value ?? "",
  };
  startLoop(
    () => {
      match.tick();
    },
    () => {
      match.render();
    },
  );
} else if (params.get("capture") === "1") {
  // Съёмка ролика 60 к/с: внешний скрипт шагает мир по кадру и снимает холст (tools/capture)
  window.__arena = {
    advance(frames: number) {
      match.stepBots(frames);
      match.render();
    },
    settle() {
      match.settleCamera();
    },
    frame: () => match.snapshot.frame,
    measurements: () => match.measurements(),
    probe: () => match.probe(),
    sparks: () => match.sparks(),
    reach: () => match.reachTable(),
    audioMark: () => {
      match.markAudio();
    },
    audioTrack: () => match.audioTrack(),
    audioCues: () => match.audioCues(),
    reactions: (frames: number) => match.reactionTable(frames),
    limbsAt: (clip: string, frame: number) => match.limbsAt(clip, frame),
    art: () => match.artCanvas.toDataURL("image/png"),
    background: () => match.backgroundCanvas().toDataURL("image/png"),
    ready: () => match.spritesReady,
    fighters: () =>
      match.snapshot.fighters.map((fighter) => ({
        x: fighter.x / 256,
        y: fighter.y / 256,
        state: fighter.state,
        health: fighter.health,
        ability: fighter.ability,
        abilityFrame: fighter.abilityFrame,
        stateFrame: fighter.stateFrame,
        slowmo: match.snapshot.slowmo,
        slowTick: match.snapshot.slowTick,
      })),
    content: contentHash(),
  };
  match.measurements();
  match.render();
} else {
  window.__local = { report: () => match.report() };
  // звук — с первого жеста игрока (клавиша, клик, касание): браузер не даёт звучать раньше
  const unlock = (): void => {
    match.unlockAudio();
  };
  for (const kind of ["keydown", "pointerdown", "touchstart"] as const) window.addEventListener(kind, unlock);
  startLoop(
    () => {
      match.tick();
    },
    () => {
      match.render();
    },
  );
}
